import * as THREE from 'three';
import type { AbilityId, AmbienceKind, ClassId } from '@arena/shared';
import { settings } from '../lib/settings.svelte';
import {
  AMBIENCE,
  SOUNDS,
  SOUND_IDS,
  footfallSound,
  loopFile,
  musicFile,
  soundFile,
  voice,
  type Footfall,
  type LoopId,
  type MusicId,
  type SoundId,
  type Surface,
} from './sounds';

/** Full volume this close to the listener, fading linearly to silence at `HEARING`. */
const NEAR = 4;
const HEARING = 50;
/** Critter steps are full volume this close, silent past `PATTER_HEARING`, so only nearby ones are heard. */
const PATTER_NEAR = 2;
const PATTER_HEARING = 12;
const CAST_FADE = 0.08;
/** One ability landing on one target this close together is one hit, like a spell's damage and its debuff. */
const SAME_HIT = 0.1;
const VOLUME = {
  footfall: 0.3,
  patter: 0.15,
  cast: 0.55,
  release: 0.8,
  impact: 0.8,
  fizzle: 0.5,
  click: 0.5,
  challenge: 0.8,
  ambience: 1,
};
/** Within this many ms, a control that clicks and opens a menu plays one click, not two. */
const CLICK_GAP = 50;
let lastClick = 0;

const buffers = new Map<SoundId, AudioBuffer[]>();
let loading = false;

/** Fetches every sound once per page, in the background; each is silent until its files arrive. */
function load(): void {
  if (loading) return;
  loading = true;
  const loader = new THREE.AudioLoader();
  for (const id of SOUND_IDS)
    SOUNDS[id].forEach((_, i) =>
      loader.load(soundFile(id, i), (buffer) => {
        const list = buffers.get(id) ?? [];
        list.push(buffer);
        buffers.set(id, list);
      }),
    );
}

/** An interface sound. Not positional, so it plays outside a game too. */
function ui(id: 'click' | 'challenge'): void {
  load();
  const context = THREE.AudioContext.getContext();
  if (context.state === 'suspended') void context.resume();
  const buffer = buffers.get(id)?.[0];
  if (!buffer) return;
  const gain = context.createGain();
  gain.gain.value = settings.volume * settings.interfaceVolume * VOLUME[id];
  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(gain).connect(context.destination);
  source.start();
}

/** The interface click: buttons, menus opening. */
export function click(): void {
  const now = performance.now();
  if (now - lastClick < CLICK_GAP) return;
  lastClick = now;
  ui('click');
}

/** Someone challenging us to a duel. */
export function challenge(): void {
  ui('challenge');
}

const MUSIC_FADE = 2;
/** Streamed rather than decoded: a whole song as an `AudioBuffer` is ~60 MB. */
let song: {
  id: MusicId;
  audio: HTMLAudioElement;
  gain: GainNode;
  volume: number;
} | null = null;
let bassline: AnalyserNode | null = null;
const bassSamples = new Float32Array(2048);

/** Keeps `id` playing on loop, fading out the song before it. Call it every frame: it applies volume changes, and starts the song once the page has had a gesture. */
export function music(id: MusicId | null): void {
  const context = THREE.AudioContext.getContext();
  if (!bassline) {
    bassline = context.createAnalyser();
    bassline.fftSize = bassSamples.length;
  }
  if (song?.id !== id) {
    const old = song;
    song = null;
    if (old) {
      old.gain.gain.setTargetAtTime(0, context.currentTime, MUSIC_FADE / 4);
      setTimeout(() => old.audio.pause(), MUSIC_FADE * 1000);
    }
    if (!id) return;
    const audio = new Audio(musicFile(id));
    audio.loop = true;
    const gain = context.createGain();
    gain.gain.value = 0;
    const source = context.createMediaElementSource(audio);
    source.connect(gain).connect(context.destination);
    const lowpass = context.createBiquadFilter();
    lowpass.frequency.value = 150;
    source.connect(lowpass).connect(bassline);
    song = { id, audio, gain, volume: 0 };
  }
  if (!song) return;
  if (song.audio.paused && context.state === 'running')
    song.audio.play().catch(() => {});
  const volume = settings.volume * settings.musicVolume;
  if (volume === song.volume) return;
  song.volume = volume;
  song.gain.gain.setTargetAtTime(volume, context.currentTime, 0.1);
}

/** Where the music is in its song, in seconds; 0 before it starts. */
export const musicTime = (): number => song?.audio.currentTime ?? 0;

/** How loud the music's bass is right now (RMS, about 0 to 0.3), whatever the volume settings. */
export function bass(): number {
  if (!bassline) return 0;
  bassline.getFloatTimeDomainData(bassSamples);
  let sum = 0;
  for (const s of bassSamples) sum += s * s;
  return Math.sqrt(sum / bassSamples.length);
}

/** Positional sound effects, heard from the player's unit facing the way the camera does. */
export class Sfx {
  /** Add to the scene so it follows `listen`. */
  readonly listener = new THREE.AudioListener();
  private casts = new Map<string, THREE.PositionalAudio>();
  private hits = new Map<string, number>();
  private loops = new Map<LoopId, THREE.Audio>();
  private volume = -1;
  private scratch = new THREE.Vector3();

  constructor() {
    load();
  }

  dispose(): void {
    for (const loop of this.loops.values()) if (loop.isPlaying) loop.stop();
    this.loops.clear();
    this.listener.gain.disconnect();
  }

  /** Keeps the map's background loop going, crossfading to its night loop as `night` goes 0 to 1. */
  ambience(kind: AmbienceKind, night: number): void {
    const [day, dark] = AMBIENCE[kind];
    for (const id of new Set([day, dark])) {
      let loop = this.loops.get(id);
      if (!loop) {
        const audio = new THREE.Audio(this.listener).setLoop(true);
        this.loops.set(id, audio);
        new THREE.AudioLoader().load(loopFile(id), (buffer) => {
          if (this.loops.get(id) !== audio) return;
          audio.setBuffer(buffer).play();
        });
        loop = audio;
      }
      const mix = day === dark ? 1 : id === day ? 1 - night : night;
      const volume = VOLUME.ambience * settings.ambienceVolume * mix;
      if (Math.abs(loop.getVolume() - volume) > 0.01) loop.setVolume(volume);
    }
  }

  /** Browsers start audio suspended until the page gets a gesture. */
  resume(): void {
    if (this.listener.context.state === 'suspended')
      void this.listener.context.resume();
  }

  listen(at: THREE.Vector3, camera: THREE.Camera): void {
    this.listener.position.copy(at);
    this.listener.quaternion.copy(camera.quaternion);
    if (settings.volume === this.volume) return;
    this.volume = settings.volume;
    this.listener.setMasterVolume(this.volume);
  }

  /** Plays one variant of `id` from `at`, following it as it moves. */
  play(
    id: SoundId,
    at: THREE.Object3D | undefined,
    volume = 1,
    delay = 0,
  ): THREE.PositionalAudio | null {
    const variants = buffers.get(id);
    // Sounds started while suspended would all burst out at once on resume.
    if (this.listener.context.state !== 'running') return null;
    if (!at || !variants?.length) return null;
    const pos = at.getWorldPosition(this.scratch);
    if (pos.distanceTo(this.listener.position) > HEARING) return null;
    const sound = new THREE.PositionalAudio(this.listener);
    sound.setBuffer(variants[Math.floor(Math.random() * variants.length)]);
    sound.setDistanceModel('linear');
    sound.setRefDistance(NEAR);
    sound.setMaxDistance(HEARING);
    sound.setVolume(volume * settings.effectsVolume);
    sound.setPlaybackRate(0.94 + Math.random() * 0.12);
    // The panner otherwise glides in from the origin over the first frame, smearing the attack.
    sound.panner.positionX.value = pos.x;
    sound.panner.positionY.value = pos.y;
    sound.panner.positionZ.value = pos.z;
    sound.onEnded = () => {
      sound.isPlaying = false;
      sound.removeFromParent();
      sound.disconnect();
      sound.gain.disconnect();
    };
    at.add(sound);
    sound.play(delay);
    return sound;
  }

  castStart(
    sourceId: string,
    at: THREE.Object3D | undefined,
    abilityId: AbilityId,
  ): void {
    this.castStop(sourceId);
    const id = voice(abilityId).cast;
    const sound = id && this.play(id, at, VOLUME.cast);
    if (sound) this.casts.set(sourceId, sound);
  }

  /** Fades out the unit's cast sound, if it's still going. */
  castStop(sourceId: string): void {
    const sound = this.casts.get(sourceId);
    this.casts.delete(sourceId);
    if (!sound?.isPlaying || !sound.source) return;
    const now = this.listener.context.currentTime;
    sound.gain.gain.setTargetAtTime(0, now, CAST_FADE / 3);
    sound.source.stop(now + CAST_FADE);
  }

  castSuccess(
    sourceId: string,
    at: THREE.Object3D | undefined,
    abilityId: AbilityId,
  ): void {
    this.castStop(sourceId);
    const id = voice(abilityId).release;
    if (id) this.play(id, at, VOLUME.release);
  }

  /** A cast that stopped short of going off. */
  fizzle(sourceId: string, at: THREE.Object3D | undefined): void {
    this.castStop(sourceId);
    this.play('fizzle', at, VOLUME.fizzle);
  }

  /** `abilityId` landing on a target; `blocked` when a shield soaked all of it. */
  impact(
    targetId: string,
    at: THREE.Object3D | undefined,
    abilityId: AbilityId,
    blocked = false,
  ): void {
    const key = `${targetId}:${abilityId}`;
    const now = this.listener.context.currentTime;
    if (now - (this.hits.get(key) ?? -Infinity) < SAME_HIT) return;
    this.hits.set(key, now);
    const sounds = voice(abilityId);
    const id = blocked ? (sounds.blocked ?? sounds.impact) : sounds.impact;
    if (id) this.play(id, at, VOLUME.impact);
  }

  /** An auto-attack; a ranged one lands once its arrow has flown `flight` seconds. */
  swing(
    source: THREE.Object3D | undefined,
    target: THREE.Object3D | undefined,
    ranged: boolean,
    blocked: boolean,
    flight: number,
  ): void {
    if (ranged) {
      this.play('bow-shot', source, VOLUME.release);
      this.play(blocked ? 'bow-blocked' : 'bow-hit', target, 1, flight);
      return;
    }
    this.play('swing', source, VOLUME.release);
    this.play(blocked ? 'blocked' : 'hit', target, VOLUME.impact);
  }

  footfall(
    at: THREE.Object3D,
    surface: Surface,
    footfall: Footfall,
    classId: ClassId,
  ): void {
    this.play(footfallSound(surface, footfall, classId), at, VOLUME.footfall);
  }

  /** A critter's step: a soft unarmored footfall, pitched up and only heard close by. */
  patter(at: THREE.Object3D, surface: Surface): void {
    const pos = at.getWorldPosition(this.scratch);
    if (pos.distanceTo(this.listener.position) > PATTER_HEARING) return;
    const sound = this.play(
      footfallSound(surface, 'walk', 'rogue'),
      at,
      VOLUME.patter,
    );
    sound?.setRefDistance(PATTER_NEAR);
    sound?.setMaxDistance(PATTER_HEARING);
    sound?.setPlaybackRate(1.5 + Math.random() * 0.3);
  }
}
